import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-10";
const ANSWER =
  "Shopify Plus lohnt sich, wenn Sie an konkrete Grenzen des Standardtarifs stoßen: tiefere Eingriffe in den Kaufabschluss, mehrere Shops oder Märkte parallel, echte B2B-Funktionen mit Preislisten und Kundengruppen oder Automatisierung wiederkehrender Abläufe. Reines Umsatzwachstum allein ist kein Grund für den Wechsel.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/wann-lohnt-sich-shopify-plus";
const TITLE = "Wann lohnt sich Shopify Plus? Kurze, klare Antwort";
const DESCRIPTION =
  "Wann sich Shopify Plus lohnt: Kaufabschluss anpassen, B2B, mehrere Shops und Automatisierung. Woran Sie den richtigen Zeitpunkt erkennen und wann der Standardtarif reicht.";

const faqs = [
  {
    q: "Wann lohnt sich Shopify Plus?",
    a: "Wenn Sie den Kaufabschluss tiefer anpassen müssen, mehrere Shops oder Märkte parallel betreiben, echte B2B-Funktionen brauchen oder Abläufe automatisieren wollen. Reines Umsatzwachstum allein ist kein Grund.",
  },
  {
    q: "Was kann Shopify Plus, das der Standardtarif nicht kann?",
    a: "Weitergehende Eingriffe in den Kaufabschluss, mehrere verbundene Shops, B2B-Funktionen mit Preislisten und Kundengruppen sowie Automatisierungen für wiederkehrende Abläufe.",
  },
  {
    q: "Was kostet Shopify Plus?",
    a: "Der Tarif wird individuell vereinbart und liegt deutlich über den Standardtarifen. Rechnen Sie zusätzlich mit Umsetzungsaufwand, denn die Funktionen wirken erst, wenn sie eingerichtet sind.",
  },
  {
    q: "Wann reicht der normale Tarif?",
    a: "Wenn Ihr Problem nicht der Tarif ist, sondern Produktseiten, Ladezeit, mobile Nutzung oder der Warenkorb. Diese Punkte lassen sich im Standardtarif beheben und wirken meist schneller.",
  },
  {
    q: "Wie läuft ein Wechsel ab?",
    a: "Der Shop bleibt bestehen, der Tarif wird umgestellt. Aufwand entsteht bei den neuen Funktionen: Kaufabschluss, B2B-Struktur, Automatisierungen und Tests. Das planen wir vorab, statt es nach dem Wechsel zu entdecken.",
  },
];

export const Route = createFileRoute("/fragen/wann-lohnt-sich-shopify-plus")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Plus",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Wann lohnt sich Shopify Plus?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Shopify Plus"
      title="Wann lohnt sich Shopify Plus?"
      intro="Kurz gesagt: wenn Sie an konkrete Grenzen des Standardtarifs stoßen. Der Wechsel löst technische Einschränkungen, er löst keine Probleme bei Produktseiten, Ladezeit oder Nachfrage."
      source="frage-shopify-plus"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Die vier typischen Gründe",
          p: [],
          list: [
            "Der Kaufabschluss soll tiefer angepasst werden",
            "Mehrere Shops, Länder oder Marken laufen parallel",
            "B2B mit Preislisten, Kundengruppen und Freigaben",
            "Wiederkehrende Abläufe sollen automatisiert werden",
          ],
        },
        {
          h: "Kein Grund für den Wechsel",
          p: [
            "Umsatzwachstum allein, ein neues Design oder der Wunsch nach mehr Geschwindigkeit. Diese Punkte lassen sich im bestehenden Tarif angehen und wirken dort schneller und günstiger.",
          ],
        },
        {
          h: "Womit Sie rechnen sollten",
          p: [
            "Neben dem höheren Tarif entsteht Umsetzungsaufwand: Der Kaufabschluss, B2B-Strukturen und Automatisierungen müssen eingerichtet und getestet werden. Wer das erst nach dem Wechsel plant, zahlt zweimal.",
          ],
        },
        {
          h: "Der pragmatische Weg",
          p: [
            "Schreiben Sie die drei Dinge auf, die Ihr heutiger Shop nicht kann. Wenn davon mindestens zwei tatsächlich am Tarif hängen, lohnt der Wechsel. Sonst arbeiten wir zuerst am Shop selbst.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-plus", label: "Shopify Plus Umsetzung" },
        { to: "/ratgeber/shopify-b2b-shop-einrichten", label: "B2B-Shop einrichten" },
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
      ]}
    />
  );
}
